import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { appThemes } from '~/theme';

import { CssBaseline, ThemeProvider } from '@mui/material';
import type { Theme } from '@mui/material/styles';

export const THEME_MODE_STORAGE_KEY = 'adminThemeMode';

export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
} as const;

export type ThemeMode = (typeof THEME_MODES)[keyof typeof THEME_MODES];

const DEFAULT_THEME_MODE = THEME_MODES.LIGHT;

interface ThemeModePreferenceContextValue {
  themeMode: ThemeMode;
  setThemeMode: (themeMode: ThemeMode) => void;
}

interface ThemeModePreferenceProviderProps {
  children: ReactNode;
  themes?: Record<ThemeMode, Theme>;
}

const ThemeModePreferenceContext = createContext<ThemeModePreferenceContextValue>({
  themeMode: DEFAULT_THEME_MODE,
  setThemeMode: () => undefined,
});

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === THEME_MODES.LIGHT || value === THEME_MODES.DARK;
}

function readStoredThemeMode(): ThemeMode {
  try {
    const item = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
    const parsed = item ? JSON.parse(item) : DEFAULT_THEME_MODE;

    return isThemeMode(parsed) ? parsed : DEFAULT_THEME_MODE;
  } catch (error) {
    console.warn(`Error reading localStorage key "${THEME_MODE_STORAGE_KEY}":`, error);
    return DEFAULT_THEME_MODE;
  }
}

export function ThemeModePreferenceProvider({ children, themes = appThemes }: ThemeModePreferenceProviderProps) {
  const [themeMode, setStoredThemeMode] = useState<ThemeMode>(readStoredThemeMode);
  const selectedTheme = themes[themeMode];

  const setThemeMode = useCallback((nextThemeMode: ThemeMode) => {
    try {
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, JSON.stringify(nextThemeMode));
    } catch (error) {
      console.warn(`Error setting localStorage key "${THEME_MODE_STORAGE_KEY}":`, error);
    }

    setStoredThemeMode(nextThemeMode);
  }, []);

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key !== THEME_MODE_STORAGE_KEY) {
        return;
      }

      setStoredThemeMode(readStoredThemeMode());
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => window.removeEventListener('storage', handleStorageEvent);
  }, []);

  const contextValue = useMemo(
    () => ({
      themeMode,
      setThemeMode,
    }),
    [setThemeMode, themeMode],
  );

  return (
    <ThemeModePreferenceContext.Provider value={contextValue}>
      <ThemeProvider theme={selectedTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModePreferenceContext.Provider>
  );
}

export default function useThemeModePreference() {
  return useContext(ThemeModePreferenceContext);
}
